import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { BehaviorSubject } from 'rxjs';
import { User } from '@go-modules/interfaces/user';
import { AuthProvider } from '@go-modules/interfaces/auth-providers';
import { openManagedMatDialog } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { ActiveBrandService } from '@go-modules/services/active-brand';

export interface UnlinkSsoDialogData {
	requiresPassword: boolean;
	authProvider: AuthProvider;
	dialogTitleId?: string;
}
@Component({
	selector: 'unlink-sso-dialog',
	templateUrl: './unlink-sso-dialog.component.html',
	styleUrls: ['./unlink-sso-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class UnlinkSsoDialogComponent implements OnDestroy {
	public loading$ = new BehaviorSubject(false);
	public error$ = new BehaviorSubject(false);

	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<UnlinkSsoDialogData>
	): MatDialogRef<UnlinkSsoDialogComponent, User | undefined> {
		const dialogTitleId = config?.data?.dialogTitleId ?? UnlinkSsoDialogComponent.getDialogTitleId();

		return openManagedMatDialog(dialog, UnlinkSsoDialogComponent, {
			...(config ?? {}),
			data: {
				...(config?.data ?? {}),
				dialogTitleId
			},
			ariaLabelledBy: dialogTitleId
		});
	}

	private static getDialogTitleId (): string {
		return `unlink-sso-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	}

	public constructor (
		public dialogRef: MatDialogRef<UnlinkSsoDialogComponent, User | undefined>,
		private ngxAuthService: NgxAuthService,
		@Inject(MAT_DIALOG_DATA) public data: UnlinkSsoDialogData,
		private activeBrandService: ActiveBrandService
	) {
		this.data.dialogTitleId ??= UnlinkSsoDialogComponent.getDialogTitleId();
	}

	public get translationParams () {
		return {
			...this.activeBrandService.getTranslationParams(),
			provider: this.data.authProvider.open_id_configuration.display_name
		};
	}

	public ngOnDestroy () {
		this.loading$.complete();
		this.error$.complete();
	}

	public confirm () {

		if (this.data.requiresPassword) {
			return this.dialogRef.close(null);
		}

		this.loading$.next(true);
		this.error$.next(false);

		this.ngxAuthService.unlinkSsoAccount(this.data.authProvider.provider_name).subscribe({
			next: (userResponse) => {
				this.dialogRef.close(userResponse);
			},
			error: () => {
				this.loading$.next(false);
				this.error$.next(true);
			}
		});
	}

	public cancel () {
		this.dialogRef.close(null);
	}
}
